import {
  db,
  cardStateKey,
  makeDirection,
  parseDirection,
  type CardState,
  type Direction,
} from "./db";
import { enqueueInTx, scheduleFlush } from "./sync/engine";
import { dateKey } from "./activity";
import type { LanguageId } from "./references";

/**
 * Pack-backed study queue. `CardState` rows are created lazily — a concept
 * appears as a "new" card the first time its (concept, direction) pair is
 * shown, and only persists state after the first grade.
 */

export type StudyCard = {
  conceptId: string;
  direction: Direction;
  /** Surface form on the prompt side. */
  front: string;
  /** Expected surface form on the answer side (first canonical text). */
  back: string;
  /** Acceptable alternate answers (synonyms / aliases). */
  backAliases: string[];
  /** Null for cards that haven't been graded yet. */
  state: CardState | null;
};

/**
 * The user-facing unit of Practice. Dashboard promises this exact plan and
 * Drill renders the same `cards` array; bucket counts are descriptive only.
 */
export type PracticePlan = {
  cards: StudyCard[];
  due: number;
  new: number;
  total: number;
  directionLabels: Direction[];
};

/** One Dashboard promise and one Practice batch use the same upper bound. */
export const PRACTICE_SESSION_LIMIT = 30;

/** All ordered (a→b) pairs for the user's active language set. */
export function directionsFor(langs: LanguageId[]): Direction[] {
  const out: Direction[] = [];
  for (const a of langs) {
    for (const b of langs) {
      if (a !== b) out.push(makeDirection(a, b));
    }
  }
  return out;
}

/** Intersect the lang-derived defaults with an optional user filter. The
    filter is silently ignored if it would eliminate every direction. */
export function effectiveDirections(
  activeLangs: LanguageId[],
  activeDirections?: string[],
): Direction[] {
  const all = directionsFor(activeLangs);
  if (!activeDirections || activeDirections.length === 0) return all;
  const allow = new Set(activeDirections);
  const filtered = all.filter((d) => allow.has(d));
  return filtered.length > 0 ? filtered : all;
}

export type HomeState = "loading" | "empty" | "ready";

/**
 * What the Dashboard/Home screen should render. Personal decks (Dexie
 * `decks` + deck-scoped `cardStates`, surfaced as `state.decks`/`state.cards`
 * in store.tsx) and installed concept-packs (`db.concepts`, tracked via
 * `packs:installed` in `db.kv`) are separate storage layers for *listing*
 * purposes, but both feed the same underlying `cardStates` table and the
 * same Drill queue/due counts (see dueCounts.ts, pickDrillCards) — so the
 * Dashboard renders a single "ready" view covering both, rather than
 * branching per source. This only decides the loading/empty/ready gate.
 */
export function selectHomeState(
  bootDone: boolean,
  deckCount: number,
  installedPackCount: number,
): HomeState {
  if (!bootDone) return "loading";
  if (deckCount > 0 || installedPackCount > 0) return "ready";
  return "empty";
}

/**
 * Due subset of legacy deck-scoped cards (store.tsx `Card[]`), filtered to
 * the user's active directions. Shared by the Dashboard due-count stat and
 * the Study review queue so the two can never disagree about what's due —
 * `id` is the cardState key `${conceptId}|${direction}`, so the direction
 * is recovered from it without an extra lookup.
 */
export function selectDueLegacyCards<T extends { id: string; nextReview: number }>(
  cards: T[],
  activeLangs: LanguageId[],
  activeDirections?: string[],
): T[] {
  const allowed = new Set<string>(effectiveDirections(activeLangs, activeDirections));
  const now = Date.now();
  return cards.filter((c) => {
    const direction = c.id.split("|")[1];
    return c.nextReview <= now && allowed.has(direction);
  });
}

/**
 * SM-2 "mastered" threshold — the same bar the legacy Dashboard stat card
 * has always used (repetition past its 4th successful review AND interval
 * grown past three weeks). Exported so pack-progress and due-count helpers
 * can't drift from what "mastered" means elsewhere in the app.
 */
export const MASTERED_MIN_REPETITION = 4;
export const MASTERED_MIN_INTERVAL_DAYS = 21;

export function isMastered(state: Pick<CardState, "repetition" | "interval">): boolean {
  return state.repetition >= MASTERED_MIN_REPETITION && state.interval >= MASTERED_MIN_INTERVAL_DAYS;
}

type FormIndex = Map<string, Map<LanguageId, string[]>>;

/** Exported so dueCounts.ts can enumerate the same (concept, direction)
    candidate universe pickDrillCards uses, without duplicating the query. */
export async function buildFormIndex(conceptIds: string[]): Promise<FormIndex> {
  const idx: FormIndex = new Map();
  if (conceptIds.length === 0) return idx;
  const rows = await db.conceptForms.where("conceptId").anyOf(conceptIds).toArray();
  for (const r of rows) {
    let perLang = idx.get(r.conceptId);
    if (!perLang) {
      perLang = new Map();
      idx.set(r.conceptId, perLang);
    }
    perLang.set(r.lang, r.texts);
  }
  return idx;
}

async function pickDeckPracticeCards(
  deckId: string,
  limit: number,
  includeScheduled: boolean,
): Promise<StudyCard[]> {
  const now = Date.now();
  const states = (await db.cardStates.where("deckId").equals(deckId).toArray())
    .filter(
      (state) =>
        state.interval === 0 || state.nextReview <= now || includeScheduled,
    )
    .sort((a, b) => {
      const aDue = a.interval > 0 && a.nextReview <= now;
      const bDue = b.interval > 0 && b.nextReview <= now;
      if (aDue !== bDue) return aDue ? -1 : 1;
      if (aDue && bDue && a.nextReview !== b.nextReview) {
        return a.nextReview - b.nextReview;
      }
      const aNew = a.interval === 0;
      const bNew = b.interval === 0;
      if (aNew !== bNew) return aNew ? -1 : 1;
      return a.efactor - b.efactor;
    });
  const forms = await buildFormIndex(states.map((state) => state.conceptId));
  const cards: StudyCard[] = [];
  for (const state of states) {
    const [from, to] = parseDirection(state.direction);
    const perLang = forms.get(state.conceptId);
    const fronts = perLang?.get(from);
    const backs = perLang?.get(to);
    if (!fronts?.length || !backs?.length) continue;
    cards.push({
      conceptId: state.conceptId,
      direction: state.direction,
      front: fronts[0],
      back: backs[0],
      backAliases: backs.slice(1),
      state,
    });
    if (cards.length === limit) break;
  }
  return cards;
}

/**
 * Drill queue: highest-frequency concepts in the user's active directions.
 * Due reviews always outrank everything else (see the due-first sort below)
 * and are never excluded by the frequency cut that shapes the rest of the
 * pool — otherwise a low-frequency overdue card could vanish from Drill
 * while its word still counts as "due" on the Dashboard (dueCounts.ts).
 * New cards (no schedule, including freshly installed interval-zero rows)
 * come next; within each bucket frequency descends. Cards scheduled in the
 * future are not practice work yet and are omitted.
 *
 * The unit is always one (concept, direction) card. Dashboard counts call
 * this selector too, so a displayed session size is the exact work delivered.
 *
 * This is the primary entry point for the drill mode in Study.
 */
export async function pickDrillCards(
  activeLangs: LanguageId[],
  limit: number,
  activeDirections?: string[],
  deckId?: string,
  includeScheduled = false,
): Promise<StudyCard[]> {
  if (limit <= 0) return [];
  if (deckId) return pickDeckPracticeCards(deckId, limit, includeScheduled);
  if (activeLangs.length < 2) return [];

  const dirs = effectiveDirections(activeLangs, activeDirections);
  const now = Date.now();

  // Due reviews must always make it into the candidate pool, no matter how
  // large the installed-pack universe is — otherwise the frequency
  // truncation below could silently drop an overdue low-frequency concept,
  // and dueCounts.ts (which scans every concept, unranked) would report a
  // due count Drill can never actually deliver on. Collected up front, by
  // conceptId, so it can be unioned into the truncated pool below.
  const dirSet = new Set(dirs);
  const dueConceptIds = new Set(
    (await db.cardStates.where("nextReview").belowOrEqual(now).toArray())
      .filter(
        (s) =>
          dirSet.has(s.direction) &&
          s.interval > 0 &&
          (!deckId || s.deckId === deckId),
      )
      .map((s) => s.conceptId),
  );

  // Rank by frequency in JS rather than `db.concepts.orderBy("frequency")`:
  // IndexedDB indexes skip records whose indexed key is `undefined`, and
  // `frequency` is optional — most hand-authored packs never set it, so an
  // index-ordered query would silently return nothing for them. Pull
  // everything, sort (missing frequency treated as 0, ties keep insertion
  // order), then take more than `limit` because not every concept has forms
  // in every active language — we'll filter, then trim.
  const allConcepts = await db.concepts.toArray();
  if (allConcepts.length === 0) return [];
  allConcepts.sort((a, b) => (b.frequency ?? 0) - (a.frequency ?? 0));
  const concepts = allConcepts.slice(0, limit * 6);

  // Union in any due concepts the frequency cut left out — they ride along
  // on top of the truncated pool rather than replacing anything in it.
  if (dueConceptIds.size > 0) {
    const includedIds = new Set(concepts.map((c) => c.id));
    for (const c of allConcepts) {
      if (dueConceptIds.has(c.id) && !includedIds.has(c.id)) {
        concepts.push(c);
        includedIds.add(c.id);
      }
    }
  }

  const formIdx = await buildFormIndex(concepts.map((c) => c.id));

  // Pre-load existing states for these (concept, direction) keys in one query
  // — cheaper than a per-card db.cardStates.get loop.
  const candidateKeys: string[] = [];
  for (const c of concepts) {
    const perLang = formIdx.get(c.id);
    if (!perLang) continue;
    for (const dir of dirs) {
      const [from, to] = parseDirection(dir);
      if (!perLang.has(from) || !perLang.has(to)) continue;
      candidateKeys.push(cardStateKey(c.id, dir));
    }
  }
  const stateRows = await db.cardStates.where("key").anyOf(candidateKeys).toArray();
  const stateByKey = new Map(stateRows.map((s) => [s.key, s]));

  type Candidate = { card: StudyCard; frequency: number; isNew: boolean; isDue: boolean };
  const candidates: Candidate[] = [];

  for (const c of concepts) {
    const perLang = formIdx.get(c.id);
    if (!perLang) continue;
    for (const dir of dirs) {
      const [from, to] = parseDirection(dir);
      const fronts = perLang.get(from);
      const backs = perLang.get(to);
      if (!fronts?.length || !backs?.length) continue;
      const key = cardStateKey(c.id, dir);
      const state = stateByKey.get(key) ?? null;
      if (deckId && state?.deckId !== deckId) continue;
      candidates.push({
        card: {
          conceptId: c.id,
          direction: dir,
          front: fronts[0],
          back: backs[0],
          backAliases: backs.slice(1),
          state,
        },
        frequency: c.frequency ?? 0,
        isNew: !state || state.interval === 0,
        isDue: !!state && state.interval > 0 && state.nextReview <= now,
      });
    }
  }

  // Due reviews outrank everything else (most overdue first) — this is what
  // makes the Dashboard's due count and the Drill queue agree: a card the
  // due-count stat calls "due" always surfaces before new/not-yet-due ones
  // here. Among the rest: new cards next, then those graded least
  // successfully (lowest efactor); ties broken by frequency.
  candidates.sort((a, b) => {
    if (a.isDue !== b.isDue) return a.isDue ? -1 : 1;
    if (a.isDue && b.isDue) {
      const na = a.card.state?.nextReview ?? 0;
      const nb = b.card.state?.nextReview ?? 0;
      if (na !== nb) return na - nb;
      return b.frequency - a.frequency;
    }
    if (a.isNew !== b.isNew) return a.isNew ? -1 : 1;
    if (!a.isNew && !b.isNew) {
      const ea = a.card.state?.efactor ?? 2.5;
      const eb = b.card.state?.efactor ?? 2.5;
      if (ea !== eb) return ea - eb;
    }
    return b.frequency - a.frequency;
  });

  return candidates
    .filter(
      (candidate) =>
        candidate.isDue || candidate.isNew || includeScheduled,
    )
    .slice(0, limit)
    .map((candidate) => candidate.card);
}

export async function buildPracticePlan(
  activeLangs: LanguageId[],
  activeDirections: string[] | undefined,
  limit: number,
  deckId?: string,
  includeScheduled = false,
): Promise<PracticePlan> {
  const cards = await pickDrillCards(
    activeLangs,
    limit,
    activeDirections,
    deckId,
    includeScheduled,
  );
  return practicePlanForCards(cards);
}

function practicePlanForCards(cards: StudyCard[]): PracticePlan {
  let due = 0;
  let newCount = 0;
  const directions = new Set<Direction>();
  const now = Date.now();
  for (const card of cards) {
    directions.add(card.direction);
    if (!card.state || card.state.interval === 0) newCount += 1;
    else if (card.state.nextReview <= now) due += 1;
  }
  return {
    cards,
    due,
    new: newCount,
    total: cards.length,
    directionLabels: [...directions],
  };
}

/**
 * Materialize a persisted onboarding promise by exact CardState keys. Unlike
 * the daily selector, this intentionally keeps cards that an earlier answer
 * has already scheduled into the future, so a reload cannot shrink or reorder
 * the fixed three-card sample.
 */
export async function buildFixedPracticePlan(cardKeys: string[]): Promise<PracticePlan> {
  if (cardKeys.length === 0) return practicePlanForCards([]);
  const states = await db.cardStates.bulkGet(cardKeys);
  if (states.some((state) => !state)) {
    throw new Error("fixed practice card is missing");
  }
  const concreteStates = states as CardState[];
  const forms = await buildFormIndex(concreteStates.map((state) => state.conceptId));
  const cards = concreteStates.map((state) => {
    const [from, to] = parseDirection(state.direction);
    const perLang = forms.get(state.conceptId);
    const fronts = perLang?.get(from);
    const backs = perLang?.get(to);
    if (!fronts?.length || !backs?.length) {
      throw new Error("fixed practice card forms are missing");
    }
    return {
      conceptId: state.conceptId,
      direction: state.direction,
      front: fronts[0],
      back: backs[0],
      backAliases: backs.slice(1),
      state,
    };
  });
  return practicePlanForCards(cards);
}

/**
 * Deep-link entry point for `/drill?card=<key>` (bot "word of the day"
 * button, see supabase/functions/word-of-day). Tries a fixed one-card
 * session for that key; falls back to the regular due queue when the card
 * isn't in Dexie yet — e.g. a fresh device whose initial sync hasn't pulled
 * it down yet — rather than surfacing an error for a deep link that arrived
 * a moment too early.
 */
export async function pickCardOrDrillQueue(
  cardKey: string,
  activeLangs: LanguageId[],
  limit: number,
  activeDirections?: string[],
  deckId?: string,
  includeScheduled = false,
): Promise<StudyCard[]> {
  try {
    const plan = await buildFixedPracticePlan([cardKey]);
    if (plan.cards.length > 0) return plan.cards;
  } catch {
    // Card not resolvable locally yet — fall through to the regular queue.
  }
  return pickDrillCards(activeLangs, limit, activeDirections, deckId, includeScheduled);
}

/**
 * Due queue: concepts whose CardState.nextReview has elapsed in any of the
 * user's active directions. Limited to `limit`; new concepts can be folded
 * in by passing `includeNew` to top up below the limit.
 */
export async function pickDueCards(
  activeLangs: LanguageId[],
  limit: number,
  includeNew = true,
  activeDirections?: string[],
): Promise<StudyCard[]> {
  if (activeLangs.length < 2 || limit <= 0) return [];
  const dirs = new Set(effectiveDirections(activeLangs, activeDirections));
  const now = Date.now();

  const dueStates = await db.cardStates
    .where("nextReview")
    .belowOrEqual(now)
    .filter((s) => dirs.has(s.direction))
    .limit(limit)
    .toArray();

  const conceptIds = dueStates.map((s) => s.conceptId);
  const formIdx = await buildFormIndex(conceptIds);

  const due: StudyCard[] = [];
  for (const s of dueStates) {
    const perLang = formIdx.get(s.conceptId);
    if (!perLang) continue;
    const [from, to] = parseDirection(s.direction);
    const fronts = perLang.get(from);
    const backs = perLang.get(to);
    if (!fronts?.length || !backs?.length) continue;
    due.push({
      conceptId: s.conceptId,
      direction: s.direction,
      front: fronts[0],
      back: backs[0],
      backAliases: backs.slice(1),
      state: s,
    });
  }

  if (due.length >= limit || !includeNew) return due;
  const topUp = await pickDrillCards(activeLangs, limit - due.length, activeDirections);
  return [...due, ...topUp.filter((c) => !c.state)];
}

/**
 * SM-2 grade. Creates the CardState row on first grade (lazy materialization);
 * subsequent grades update in-place.
 *
 * `quality` is 0..5 in classic SM-2; we accept the same scale the legacy
 * store uses (1 = Again, 3 = Hard, 4 = Good, 5 = Easy).
 */
export async function gradeCardState(
  conceptId: string,
  direction: Direction,
  quality: number,
): Promise<CardState> {
  const key = cardStateKey(conceptId, direction);
  const nextState = await db.transaction("rw", [db.cardStates, db.reviewEvents, db.activity, db.outbox], async () => {
    const existing = await db.cardStates.get(key);

    const prev: CardState = existing ?? {
      key,
      conceptId,
      direction,
      nextReview: 0,
      interval: 0,
      repetition: 0,
      efactor: 2.5,
    };

    let efactor =
      prev.efactor + (0.1 - (5 - quality) * (0.08 + (5 - quality) * 0.02));
    if (efactor < 1.3) efactor = 1.3;

    let repetition = prev.repetition;
    let interval: number;
    if (quality < 3) {
      repetition = 0;
      interval = 1;
    } else {
      repetition += 1;
      if (repetition === 1) interval = 1;
      else if (repetition === 2) interval = 6;
      else interval = Math.round(prev.interval * efactor);
    }

    const next: CardState = {
      ...prev,
      interval,
      repetition,
      efactor,
      nextReview: Date.now() + interval * 24 * 60 * 60 * 1000,
    };
    const reviewedAt = Date.now();
    const reviewDate = dateKey(new Date(reviewedAt));
    const event = { id: crypto.randomUUID(), cardKey: key, deckId: prev.deckId,
      reviewedAt, reviewDate, quality, isNew: !existing || (prev.interval === 0 && prev.repetition === 0) };
    await db.cardStates.put(next);
    await db.reviewEvents.add(event);
    const activity = await db.activity.get(reviewDate);
    await db.activity.put({ date: reviewDate, reviewed: (activity?.reviewed ?? 0) + 1,
      correct: (activity?.correct ?? 0) + (quality >= 3 ? 1 : 0) });
    await enqueueInTx("cardStates", "upsert", key);
    await enqueueInTx("reviewEvents", "upsert", event.id);
    return next;
  });
  scheduleFlush();
  return nextState;
}
